import React from "react";
import {
    EMPTY_STRING,
    PASSWORD_REQUIREMENTS_ICON_SIZE,
    passwordRequirementsText,
} from "../../constants";
import PropTypes from "prop-types";
import {
    hasMinimumLength,
    charTypeHasLowerCase,
    charTypeHasUpperCase,
    charTypeHasNumbers,
    charTypeHasSpecialChars,
    hasEnoughCharacterTypes,
} from "../../helpers";
import CheckIcon from "../../../../../../ui/icons/CheckIcon";
import OIcon from "../../../../../../ui/icons/OIcon";
import XIcon from "../../../../../../ui/icons/XIcon";

export function PasswordRequirements(props) {
    const { password = EMPTY_STRING } = props;

    const hasMinLength = hasMinimumLength(password);
    const hasEnoughTypes = hasEnoughCharacterTypes(password);

    const hasLowerCase = charTypeHasLowerCase(password);
    const hasUpperCase = charTypeHasUpperCase(password);
    const hasNumbers = charTypeHasNumbers(password);
    const hasSpecialChars = charTypeHasSpecialChars(password);

    // Get CSS variables for icon colors
    const tealColor = "var(--avendaTeal1)";
    const grayColor = "var(--avendaGray1)";

    return (
        <div className="-mt-4 mb-2.5 text-sm font-thin tracking-wider">
            <ul className="mt-1.5 list-none p-0">
                <li
                    className={`relative mb-1.5 flex items-start ${hasMinLength ? "text-(--avendaTeal1)" : "text-(--avendaGray1)"}`}
                >
                    <span className="absolute top-0.5 left-0 h-4 w-4">
                        {hasMinLength ? (
                            <CheckIcon
                                className="h-full w-full"
                                customColor={tealColor}
                                size={PASSWORD_REQUIREMENTS_ICON_SIZE}
                            />
                        ) : (
                            <OIcon
                                className="h-full w-full"
                                customColor={grayColor}
                                size={PASSWORD_REQUIREMENTS_ICON_SIZE}
                            />
                        )}
                    </span>
                    <span className="pl-6">
                        {passwordRequirementsText.MIN_LENGTH}
                    </span>
                </li>
                <li
                    className={`relative mb-1.5 flex flex-col ${hasEnoughTypes ? "text-(--avendaTeal1)" : "text-(--errorRed)"}`}
                >
                    <div className="flex items-start">
                        <span className="absolute top-0.5 left-0 h-4 w-4">
                            {hasEnoughTypes ? (
                                <CheckIcon
                                    className="h-full w-full"
                                    customColor={tealColor}
                                    size={PASSWORD_REQUIREMENTS_ICON_SIZE}
                                />
                            ) : (
                                <XIcon
                                    className="h-full w-full"
                                    customColor="var(--errorRed)"
                                    size={PASSWORD_REQUIREMENTS_ICON_SIZE}
                                />
                            )}
                        </span>
                        <span className="block pl-6">
                            {
                                passwordRequirementsText.CHARACTER_TYPE_REQUIREMENTS
                            }
                        </span>
                    </div>
                    <ul className="mt-1.5 w-full list-none pl-6">
                        <li
                            className={`relative mb-0.5 flex items-start ${hasLowerCase ? "text-(--avendaTeal1)" : "text-(--avendaGray1)"}`}
                        >
                            <span className="absolute top-0.5 left-0 h-4 w-4">
                                {hasLowerCase ? (
                                    <CheckIcon
                                        className="h-full w-full"
                                        customColor={tealColor}
                                        size={PASSWORD_REQUIREMENTS_ICON_SIZE}
                                    />
                                ) : (
                                    <OIcon
                                        className="h-full w-full"
                                        customColor={grayColor}
                                        size={PASSWORD_REQUIREMENTS_ICON_SIZE}
                                    />
                                )}
                            </span>
                            <span className="pl-6">
                                {passwordRequirementsText.LOWERCASE}
                            </span>
                        </li>
                        <li
                            className={`relative mb-0.5 flex items-start ${hasUpperCase ? "text-(--avendaTeal1)" : "text-(--avendaGray1)"}`}
                        >
                            <span className="absolute top-0.5 left-0 h-4 w-4">
                                {hasUpperCase ? (
                                    <CheckIcon
                                        className="h-full w-full"
                                        customColor={tealColor}
                                        size={PASSWORD_REQUIREMENTS_ICON_SIZE}
                                    />
                                ) : (
                                    <OIcon
                                        className="h-full w-full"
                                        customColor={grayColor}
                                        size={PASSWORD_REQUIREMENTS_ICON_SIZE}
                                    />
                                )}
                            </span>
                            <span className="pl-6">
                                {passwordRequirementsText.UPPERCASE}
                            </span>
                        </li>
                        <li
                            className={`relative mb-0.5 flex items-start ${hasNumbers ? "text-(--avendaTeal1)" : "text-(--avendaGray1)"}`}
                        >
                            <span className="absolute top-0.5 left-0 h-4 w-4">
                                {hasNumbers ? (
                                    <CheckIcon
                                        className="h-full w-full"
                                        customColor={tealColor}
                                        size={PASSWORD_REQUIREMENTS_ICON_SIZE}
                                    />
                                ) : (
                                    <OIcon
                                        className="h-full w-full"
                                        customColor={grayColor}
                                        size={PASSWORD_REQUIREMENTS_ICON_SIZE}
                                    />
                                )}
                            </span>
                            <span className="pl-6">
                                {passwordRequirementsText.NUMBER}
                            </span>
                        </li>
                        <li
                            className={`relative mb-0.5 flex items-start ${hasSpecialChars ? "text-(--avendaTeal1)" : "text-(--avendaGray1)"}`}
                        >
                            <span className="absolute top-0.5 left-0 h-4 w-4">
                                {hasSpecialChars ? (
                                    <CheckIcon
                                        className="h-full w-full"
                                        customColor={tealColor}
                                        size={PASSWORD_REQUIREMENTS_ICON_SIZE}
                                    />
                                ) : (
                                    <OIcon
                                        className="h-full w-full"
                                        customColor={grayColor}
                                        size={PASSWORD_REQUIREMENTS_ICON_SIZE}
                                    />
                                )}
                            </span>
                            <span className="pl-6">
                                {passwordRequirementsText.SPECIAL_CHAR}
                            </span>
                        </li>
                    </ul>
                </li>
            </ul>
        </div>
    );
}

PasswordRequirements.propTypes = {
    password: PropTypes.string,
};

PasswordRequirements.defaultProps = {
    password: EMPTY_STRING,
};
